import React, {useLayoutEffect, useMemo, useRef, useState} from "react";
import {Bubble, Dropdown, Icon, Input, List, notification, StatusTip, Radio} from "tea-component";
import {getLastDateRange, getStaticDateText, useDblIntl} from "@/pages/datalab/utils/utils";
import { type ITableColumnDto, columnCheck } from '@dbl/services/dataSource';
import './index.scss';

import {DBColumnType} from "@/pages/datalab/components/SortableDrag/typeDefine";
import {DATE_TYPE_OPTIONS} from "./constants";
import { MetricTypes, FieldType } from '@dbl/core/dataSource';
import NumberInput from "@/pages/datalab/components/NumberInput";
import { NumericalUnit } from '@dbl/core/chartEditor';
import { getDefaultNumberUnit } from '@dbl/core/chartEditor';
import { ChartTypeDefaultUnit, FieldTypeDefaultAccuracy, type TNumericalUnit } from '@dbl/core/chartEditor';
import { ChartType, type TChartType } from '@dbl/core/chart';
import {useIntl} from "umi";
import DatalabGrid from "@/pages/datalab/components/DatalabGrid";
import TipContext from "@home/components/TipContext";

const { LoadingTip } = StatusTip;

// 重名校验
export function duplicateNameVerification(columns: ITableColumnDto[], newName: string,) {
  return columns?.some((column) => {
    const oldName = column.display_name;
    return oldName === newName;
  });
}
// 重命名输入框
export function RenameWrapper(props: any) {
  const { renderItem, className, changeRename, oldName='', setRenameSelectComp} = props;
  const [renameValue, setRenameValue] = useState<string|null>(null);
  const [content, setContent] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRef = useRef<HTMLDivElement >(null);
  const widthRef = useRef(0); // 为了重命名时input的宽度
  // eslint-disable-next-line eqeqeq
  widthRef.current = renameValue == null ? (itemRef?.current?.clientWidth || 0) - 7: widthRef.current;
  useLayoutEffect(() => {
    // eslint-disable-next-line eqeqeq
    renameValue != null && inputRef.current?.focus();
  }, [oldName, renameValue, setRenameSelectComp]);
  const clickRename = () => { setRenameValue(oldName) };
  const onBlur = () => {
    const newName = renameValue?.trim() || '';
		
    if (!newName) {
      setContent('');
      setRenameSelectComp?.('');
      setRenameValue(null);
      return;
    }
    if (newName && newName !== oldName) {
      const content = changeRename(newName);
      if (content) { // 如果返回文本内容, 说明校验出错, 显示报错信息
        setContent(content);
        inputRef.current?.focus();
        return;
      }
    }
    setContent('');
    setRenameSelectComp?.('');
    setRenameValue(null);
  };
  // eslint-disable-next-line eqeqeq
  return renameValue == null ? renderItem(itemRef, clickRename) : (
    <Bubble
      visible={!!content}
      content={content}
      className={`dbl-analysis-rename-input-error`}
    >
      <div
        className={`dbl-analysis-rename-wrapper white data-label-tips-undrag ${className} ${content ? 'border-red' : ''}`}
        ref={itemRef}
      >
        <Input
          className={`dbl-analysis-rename-input ${content ? 'error' : ''}`}
          ref={inputRef}
          style={{width: widthRef.current}}
          value={renameValue}
          onChange={(value) => {
            setRenameSelectComp?.(value);
            setRenameValue(value);
          }}
          onPressEnter={(value, event) => {
            event.preventDefault();
            onBlur();
          }}
          onBlur={onBlur}
          onMouseDown={(event) => event.stopPropagation()}
        />
        <div onMouseDown={(e) => {
          e.preventDefault();
        }}
        className={'delete-bg-icon_container'}
        >
          <Icon type="dismiss"
					 className={'delete-bg-icon'} 
					   onClick={() => setRenameValue('')} />
        </div>
      </div>
    </Bubble>
  );
}

// 重命名
function RenameComponent(props: any) {
  return (<List.Item onClick={()=> {props.onClick();props.close()}}>{props.formatMessage('rename')}</List.Item>);
}
// 转换指标/维度
function TransformComponent(props: any) {
  const { formatMessage, close, value, onChange, column } = props;
  return (<List.Item
    onClick={() => { onChange(column, {attribute: value ? 1 : 2});close()}}
  >
    {formatMessage(value ? 'convert_to_dimension' : 'convert_to_metric')}
  </List.Item>);
}
// 字段设置
/**
 * dateCheckStatus
 * undefined代表没有进行日期校验
 * loading代表正在进行日期校验
 * disabled代表日期校验失败, 无法转换成日期类型
 * 日期格式字符串代表日期可以转换的日期格式
 */
const dateCheckStatus: any = {};
function FieldTypeComponent(props: any) {
  
  const { formatMessage, close, value, decimal=2, onChange, column, customDateCheckParams, chartType = ChartType.Line } = props;
  // const [decimalOfFloat, setDecimalOfFloat] = useState<number>(value === 'float' ? decimal : 2);
  // const [decimalOfPercent, setDecimalOfPercent] = useState<number>( value === 'percent' ? decimal : 2);
  // const [open, setOpen] = useState<boolean>( false);
  const id = column.column_custom ? column.id : column.table_id+column.column_name;
  const [checkStatus, setCheckStatus] = useState<string|undefined>(dateCheckStatus[id]);
  const clickFun = (info: any, isClose?:boolean) => {
    // eslint-disable-next-line eqeqeq
    if ((value !== info.column_type || value === 'date') || ((info.accuracy != undefined) && (decimal != info.accuracy))) {
      onChange(column, info);
    }
    isClose && close();
  };
  
  const dateText = useMemo(() => {
    const buttonText = DATE_TYPE_OPTIONS?.find((item: any) => item?.value === (column?.to_date_format || 'YYYYMMDD'))?.buttonText || '';
    return `${formatMessage('date')}: ${buttonText || ''}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [column]);
  
  /**
   * List.SubMenu本身的下拉浮窗由于umi.css中的样式问题不显示, 优先级特殊性覆盖无法解决
   * 使用Popover组件在 点击选项 后, 两个浮窗消失不同步
   * Dropdown的button如果是 List.SubMenu的话, 则原来list item的样式以及hover样式都没有了
   */
  return (
    <List.Item className={'submenu'}>
      <Dropdown
        className={'dbl-analysis-edit-list-sub'}
        boxClassName={'dbl-analysis-edit-list-sub-drop field-type'}
        button={`${formatMessage('field_type')}: ${value === 'date' ? dateText : `${formatMessage(value)}`}`}
        appearance={'pure'}
        placement="right-start"
        placementOffset={[0, 9]}
        trigger={'hover'}
        clickClose={false}
        updateOnChildrenChange={true}
        // onClose={() => {
        // 	setOpen(false)
        // }}
      >
        {
          (subClose) => (<List type="option" className={'dbl-analysis-edit-list'}>
            <List.Item
              selected={value === FieldType.String}
              onClick={() => {
                subClose();
                let aggregate = MetricTypes.max.value;
                let accuracy = FieldTypeDefaultAccuracy.string;
                let unit;
                if (column.column_sensitive) {
                  // 字符串类型的聚合方式是去重数时,
                  aggregate = MetricTypes.distinct.value;
                  accuracy = column.accuracy ?? 0;
                  unit = column.unit ?? ChartTypeDefaultUnit[chartType as TChartType];
                }
                value !== FieldType.String && clickFun({column_type: FieldType.String, aggregate, operation: 'include', column_value: [], isAll: true, accuracy, unit,}, true);
              }}
            >
              {formatMessage('string')}
            </List.Item>
            <List.Item
              className={'submenu'}
              selected={value === FieldType.Date}
              disabled={checkStatus === 'disabled'}
            >
              <DateTypeComponent
                formatMessage={formatMessage}
                checkStatus={checkStatus}
                column={column}
                onClick={(toDateFormat: any) => {
                  try {
                    // 用户转换为日期时, 先校验是否可以转换
                    dateCheckStatus[id] = 'loading';
                    setCheckStatus('loading');
                    const params = (column.column_custom) ? {...customDateCheckParams, to_date_format: toDateFormat} : { table_id: column.table_id, column: column.column_name, to_date_format: toDateFormat };
                    columnCheck(params).then((result: any) => {
                      // date_format返回空字符串或者没有得到date_format, 则视为日期格式校验失败, 无法转换成日期格式
                      if (result?.date_format) {
                        dateCheckStatus[id] = result?.date_format;
                        setCheckStatus(result?.date_format);
                        subClose();
                        if (value !== FieldType.Date) {
                          // 从其他类型转换到日期类型的话, 需要设置日期筛选器的默认值
                          const dateType = column.column_granularity?.replace(/\d/, '') || 'day';
                          const values = DBColumnType.getFilterValues({ ...column, dateType, column_value: undefined, column_type: FieldType.Date });

                          const dateRangeParts = values?.[0]?.split('-') || [7, 'day'];
                          const text = getStaticDateText(getLastDateRange(dateRangeParts[0], dateRangeParts[1]), dateType);
                          
                          clickFun({ column_type: FieldType.Date, date_format: result?.date_format, to_date_format: toDateFormat, operation: 'recent', column_value: values, dateType, lastCount: Number(dateRangeParts[0]), column_text: text, isAll: false, from: '1', accuracy: FieldTypeDefaultAccuracy.date, unit: undefined, }, true);
                        } else {
                          // 日期类型 内部格式转换, 保持原来筛选器的值
                          clickFun({ column_type: FieldType.Date, date_format: result?.date_format, to_date_format: toDateFormat, isAll: false, accuracy: FieldTypeDefaultAccuracy.date, unit: undefined,}, true);
                        }
                        
                        notification.success({
                          description: formatMessage('check_date_success')
                        });
                      } else {
                        // 无法转换为日期类型
                        notification.error({
                          description: formatMessage('check_date_fail')
                        });
                        // setOpen(false)
                        dateCheckStatus[id] = 'disabled';
                        setCheckStatus('disabled');
                      }
                    }).catch(() => {
                      // 校验请求发生错误, 无法进行校验, 下一次打开时还需要校验.
                      dateCheckStatus[id] = undefined;
                      setCheckStatus(undefined);
                      // setOpen(false)
                    });
                  } catch (e) {
                  }
                }}
              />
            </List.Item>
            <List.Item
              selected={value === FieldType.Number}
              onClick={() => {
                subClose();
                value !== FieldType.Number && clickFun({column_type: FieldType.Number, aggregate: column.column_sensitive ? MetricTypes.distinct.value : MetricTypes.sum.value, accuracy: FieldTypeDefaultAccuracy.number, unit: getDefaultNumberUnit({fieldType: FieldType.Number, chartType}), operation: 'greater', column_value: [], isAll: false}, true);
              }}
              // onClick={() => { subClose();clickFun({column_type: 'number', aggregate: 'sum', operation: 'greater', column_value: [], isAll: false}, true) }}
              
            >
              {formatMessage('number')}
            </List.Item>
            <List.Item
              selected={value === FieldType.Float}
              onClick={() => {
                subClose();
                value !== FieldType.Float && clickFun({column_type: FieldType.Float, aggregate: column.aggregate, accuracy: FieldTypeDefaultAccuracy.float, unit: getDefaultNumberUnit({fieldType: FieldType.Float, chartType}), operation: 'greater', column_value: [], isAll: false}, true);
              }}
            >
              {formatMessage('float')}
            </List.Item>
            <List.Item
              selected={value === FieldType.Percent}
              onClick={() => {
                subClose();
                value !== FieldType.Percent && clickFun({column_type: FieldType.Percent, aggregate: column.aggregate, accuracy: FieldTypeDefaultAccuracy.percent, unit: getDefaultNumberUnit({fieldType: FieldType.Percent, chartType}), operation: 'greater', column_value: []}, true);
              }}
            >
              {formatMessage('percent')}
            </List.Item>
          </List>)
        }
      </Dropdown>
    
    </List.Item>
  );
}
// 时间类型设置弹窗
export function DateTypeComponent(props: any) {
  const { formatMessage, checkStatus, column, onClick } = props;
  const [dateType, setDateType] = useState(column?.to_date_format || 'YYYYMMDD');
  
  return (<Dropdown
    className={'dbl-analysis-edit-list-sub'}
    boxClassName={`dbl-analysis-edit-list-sub-drop date-type ${checkStatus === 'disabled' ? 'disabled' : ''}`}
    button={checkStatus === 'loading' ? (<LoadingTip loadingText={''} />) : formatMessage('date')}
    appearance={'pure'}
    placement="right-start"
    placementOffset={[-4, 9]}
    trigger={'hover'}
    clickClose={false}
  >
    {(dateTypeClose) => (<List type="option" className={'dbl-analysis-edit-list'}>
      {
        DATE_TYPE_OPTIONS?.map((item: any) => <List.Item
          key={item?.value}
          selected={dateType === item?.value}
          onClick={() => {
            onClick?.(item?.value);
            setDateType(item?.value);
            dateTypeClose();
          }}
        >
          {item?.text}
        </List.Item>)
      }
    </List>)
    }
  </Dropdown>);
}
// 维度值设置
function DimensionValueComponent(props: any) {
  const { formatMessage, close, value, onChange, column, } = props;
  const clickFun = (value: string) => {
    onChange(column, {dimension_setting: value});
    close();
  };
  return (
    <List.Item className={'submenu'}>
      <Dropdown
        className={'dbl-analysis-edit-list-sub'}
        boxClassName={'dbl-analysis-edit-list-sub-drop'}
        button={`${formatMessage('dimension_value_settings')}: ${formatMessage(value)}`}
        appearance={'pure'}
        placement="right-start"
        placementOffset={[-4, 9]}
        trigger={'hover'}
      >
        <List type="option">
          <List.Item
            selected={value === 'origin'}
            onClick={() => { clickFun("origin")}}
          >
            {formatMessage('origin')}
          </List.Item>
          <List.Item
            selected={value === 'system'}
            onClick={() => { clickFun("system")}}
            disabled
          >
            {formatMessage('system')}
          </List.Item>
          {/* <List.Item*/}
          {/*	selected={value === 'customize'}*/}
          {/*	onClick={() => { clickFun("customize")}}*/}
          {/* >*/}
          {/*	{formatMessage('customize')}*/}
          {/* </List.Item>*/}
        </List>
      </Dropdown>
    </List.Item>
  );
}
// 是否含维度汇总
function Include255Component(props: any) {
  
  const { formatMessage, close, value, onChange, column, } = props;
  const clickFun = (newValue: number) => {
    (value !== newValue) && onChange(column, {flag: newValue});
    close();
  };
  return (
    <List.Item className={'submenu'}>
      <Dropdown
        className={'dbl-analysis-edit-list-sub'}
        boxClassName={'dbl-analysis-edit-list-sub-drop'}
        button={`${formatMessage('include_dimension_total_value')}: ${formatMessage(value ? 'exclude' : 'include')}`}
        appearance={'pure'}
        placement="right-start"
        placementOffset={[-4, 9]}
        trigger={'hover'}
      >
        <List type="option">
          <List.Item
            selected={Number(value) === 0}
            onClick={() => { clickFun(0)}}
          >
            {formatMessage('include')}
          </List.Item>
          <List.Item
            selected={Number(value) === 1}
            onClick={() => { clickFun(1)}}
          >
            {formatMessage('exclude')}
          </List.Item>
        </List>
      </Dropdown>
    </List.Item>
  );
}

// BMK 和 小数位数设置
interface IBMKAndDecimalProps {
  onChange: (column: any, updateData: { unit?: string; accuracy?: number; }) => void;
  accuracy?: string | number; // 小数位数
  unit?: TNumericalUnit; // bmk单选设置的值
  disabled?: boolean; // 禁用整个设置
  column: any; // 字段信息对象
  hideUnit?: boolean; // 百分比类型下 不显示 bmk单选设置
}

export function BMKAndDecimalComponent(props: IBMKAndDecimalProps): React.ReactElement {
  const { formatMessage } = useIntl();
  const { onChange, accuracy = '2', unit = NumericalUnit.BMK, disabled, column, hideUnit } = props;

  const handleRadioChange = (value: string): void => {
    onChange(column, { unit: value, accuracy: Number(accuracy) });
  };

  const handleNumberChange = (value: string): void => {
    if (value) {
      onChange(column, {
        accuracy: Number(value),
        unit,
      });
    }
  };
  
  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>): void => {
    // 避免点击input框时关闭弹窗
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
  };

  const handleInputMouseDown = (event: React.MouseEvent<HTMLInputElement>): void => {
    // 解决左侧字段列表中的input框无法获取焦点问题
    // 不要自己 preventDefault，只是阻断冒泡到祖先
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
  };

  return (
    <List.Item className={'submenu'}>
      <Dropdown
        trigger={'hover'}
        appearance={'pure'}
        placement="right-end"
        placementOffset={[6, 9]}
        clickClose={false}
        disabled={disabled}
        button={
          <>
            {formatMessage({ id: 'dbl_multi_display_format' })}
            {disabled
              ? formatMessage({ id: 'dbl_multi_not_applicable' })
              : (`${hideUnit ? '' : `${formatMessage({ id: `dbl_multi_unit_${unit}` })},`  }${formatMessage({id: 'dbl_multi_decimal'})} ${accuracy}`)
            }
          </>
        }
        updateDeps={[accuracy, unit]}
        className={'dbl-display-format-box dbl-analysis-edit-list-sub'}
        overlayClassName={'dbl-display-format-overlay'}
      >
        <DatalabGrid colCount={2} gap="20px">
          {hideUnit ? (
            ''
          ) : (
            <>
              {formatMessage({ id: 'dbl_multi_unit' })}
              <Radio.Group value={unit} onChange={handleRadioChange}>
                <Radio name={NumericalUnit.BMK}>
                  {formatMessage({ id: 'dbl_multi_unit_bmk' })}
                  <TipContext
                    className={'dbl-display-format-bmk-info'}
                    tipText={formatMessage({ id: 'dbl_multi_unit_bmk_info' })}
                  />
                </Radio>
                <Radio name={NumericalUnit.ORIGINAL}>
                  {formatMessage({ id: 'dbl_multi_unit_thousandsDecimals' })}
                </Radio>
              </Radio.Group>
            </>
          )}
          {formatMessage({ id: 'dbl_multi_decimal' })}
          <NumberInput
            className={'dbl-display-format-input'}
            type="number"
            value={accuracy}
            onChange={handleNumberChange}
            allowNegative={false}
            allowDecimal={false}
            min={0}
            onClick={handleInputClick}
            onMouseDown={handleInputMouseDown}
          />
        </DatalabGrid>
      </Dropdown>
    </List.Item>
  );
}

interface EditComponents {
  rename: (props: any) => React.ReactElement;
  transform: (props: any) => React.ReactElement;
  fieldType: (props: any) => React.ReactElement;
  dimensionValue: (props: any) => React.ReactElement;
  include255: (props: any) => React.ReactElement;
  displayFormat: (props: any) => React.ReactElement;
  custom: (props: { render: () => React.ReactElement; }) => React.ReactElement;
}
const EDIT_COMPONENTS: EditComponents = {
  rename: RenameComponent,
  transform: TransformComponent,
  fieldType: FieldTypeComponent,
  dimensionValue: DimensionValueComponent,
  include255: Include255Component,
  displayFormat: BMKAndDecimalComponent,
  custom (props: {render: () => React.ReactElement;}) {
    // 外界自定义组件, 通过传递render函数进行渲染
    return props.render();
  }
};


export function EditList(props: any) {
  const { options=[], close, column } = props;
  const { dblFormatMessage } = useDblIntl("multi");
  
  return (
    <List type={'option'} className={'dbl-analysis-edit-list'}>
      {
        options.map((opt: any, index: number) => {
          const { type, ...other} = opt;
          return React.createElement(EDIT_COMPONENTS[type as keyof EditComponents], {
            key: type+index,
            formatMessage: dblFormatMessage,
            close,
            column,
            ...other,
            
          });
        })
      }
    </List>
  );
}